import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import type { UiState } from 'instantsearch.js';
import { algoliaAttributes } from 'assets/constants';
import cloneDeep from 'lodash/cloneDeep';
import { sanitizeString } from 'helpers';

type RouteState = {
  query?: string;
  page?: string;
  city?: string[];
  cityCode?: string[];
  groupCode?: string[];
  operationName?: string[];
  operationCode?: string[];
  sortBy?: string;
  hitsPerPage?: string;
  loc?: string | undefined | null;
  fullName?: string[];
  r?: number | "all" | undefined;
};

const routeStateDefaultValues: RouteState = {
  query: '',
  page: '1',
  city: undefined,
  cityCode: undefined,
  groupCode: undefined,
  operationName: undefined,
  operationCode: undefined,
  sortBy: 'instant_search',
  hitsPerPage: '20',
  loc: undefined,
  fullName: undefined,
  r: 10000,
};

const router = historyRouter<RouteState>({

  createURL({ qsModule, routeState, location }): string {
    const { protocol, hostname, port = '', pathname, hash } = location;
    const portWithPrefix = port === '' ? '' : `:${port}`;
    const baseUrl = `${protocol}//${hostname}${portWithPrefix}${pathname}`;

    const queryParameters: Partial<RouteState> = {};

    if (
      routeState.query &&
      routeState.query !== routeStateDefaultValues.query
    ) {
      queryParameters.query = encodeURIComponent(routeState.query);
    }
    if (routeState.page && routeState.page !== routeStateDefaultValues.page) {
      queryParameters.page = routeState.page;
    }

    if (
      routeState.city &&
      routeState.city !== routeStateDefaultValues.city
    ) {
      queryParameters.city = routeState.city.map(encodeURIComponent);
    }

    if (routeState.fullName && routeState.fullName !== routeStateDefaultValues.fullName) {
      queryParameters.fullName = routeState.fullName.map(encodeURIComponent);
    }

    if (
      routeState.operationName &&
      routeState.operationName !== routeStateDefaultValues.operationName
    ) {
      queryParameters.operationName = routeState.operationName;
    }

    if (
      routeState.operationCode &&
      routeState.operationCode !== routeStateDefaultValues.operationCode
    ) {
      queryParameters.operationCode = routeState.operationCode;
    }

    if (
      routeState.groupCode &&
      routeState.groupCode !== routeStateDefaultValues.groupCode
    ) {
      queryParameters.groupCode = routeState.groupCode;
    }

    if (
      routeState.cityCode &&
      routeState.cityCode !== routeStateDefaultValues.cityCode
    ) {
      queryParameters.cityCode = routeState.cityCode;
    }


    if (
      routeState.sortBy &&
      routeState.sortBy !== routeStateDefaultValues.sortBy
    ) {
      queryParameters.sortBy = routeState.sortBy;
    }
    if (
      routeState.hitsPerPage &&
      routeState.hitsPerPage !== routeStateDefaultValues.hitsPerPage
    ) {
      queryParameters.hitsPerPage = routeState.hitsPerPage;
    }
    if (
      routeState.loc &&
      routeState.loc !== routeStateDefaultValues.loc
    ) {
      queryParameters.loc = routeState.loc;
      queryParameters.r = routeState.r;
    }

    const queryString = qsModule.stringify(queryParameters, {
      addQueryPrefix: true,
      arrayFormat: 'repeat',
    });

    return `${baseUrl}${queryString}${hash}`;
  },

  parseURL({ qsModule, location }): RouteState {
    const queryParameters = qsModule.parse(location.search.slice(1));
    const {
      query = '',
      page = 1,
      city,
      cityCode,
      operationCode,
      operationName,
      groupCode,      
      hitsPerPage,
      sortBy,
      loc,
      fullName,
      r
    } = queryParameters;

    const allcityCode = (
      Array.isArray(cityCode) ? cityCode : [cityCode].filter(Boolean)
    ) as string[];

    const allcities = (
      Array.isArray(city) ? city : [city].filter(Boolean)
    ) as string[];

    const allOperationName = (
      Array.isArray(operationName) ? operationName : [operationName].filter(Boolean)
    ) as string[];

    const allOperationCode = (
      Array.isArray(operationCode) ? operationCode : [operationCode].filter(Boolean)
    ) as string[];

    const allgroupCode = (
      Array.isArray(groupCode) ? groupCode : [groupCode].filter(Boolean)
    ) as string[];

    const allfullName = (
      Array.isArray(fullName) ? fullName : [fullName].filter(Boolean)
    ) as string[];

    return {
      query: decodeURIComponent(query as string),
      page: page as string,
      city: allcities.map(decodeURIComponent),
      cityCode: allcityCode.map(decodeURIComponent),
      operationCode: allOperationCode.map(decodeURIComponent),
      operationName: allOperationName.map(decodeURIComponent),
      groupCode: allgroupCode.map(decodeURIComponent),
      sortBy: sortBy as string,
      hitsPerPage: hitsPerPage as string,
      loc: decodeURIComponent(loc as string),
      fullName: allfullName.map(decodeURIComponent),
      r: r ? parseInt(r as string) : 10000,
    };
  },
});

const getStateMapping = ({ indexName }: { indexName: string }) => ({
  stateToRoute(uiState: UiState): RouteState {
    const indexUiState = uiState[indexName];
    const exactUiState = uiState['exact'];
    let aroundRadiusMatches;
    let arroundLatLngMatches;
    let cityCodesMatches;
    if (exactUiState != undefined) {
      aroundRadiusMatches = exactUiState?.configure?.aroundRadius;
      arroundLatLngMatches = exactUiState?.configure?.aroundLatLng;
      cityCodesMatches = exactUiState?.refinementList && exactUiState?.refinementList[algoliaAttributes.AddressCityCode];
    }
    else {
      aroundRadiusMatches = indexUiState?.configure?.aroundRadius;
      arroundLatLngMatches = indexUiState?.configure?.aroundLatLng;
      cityCodesMatches = indexUiState?.refinementList && indexUiState?.refinementList[algoliaAttributes.AddressCityCode];
    }

    return {
      query: indexUiState.query,
      page: (indexUiState.page && String(indexUiState.page)) || undefined,
      city: indexUiState.refinementList && indexUiState.refinementList['address.city'],
      cityCode: cityCodesMatches,
      //medicalServices: indexUiState.refinementList && indexUiState.refinementList['medicalServices'],
      groupCode: indexUiState.refinementList && indexUiState.refinementList[algoliaAttributes.GroupCode],
      operationName: indexUiState.refinementList && indexUiState.refinementList[algoliaAttributes.OperationName],
      operationCode: indexUiState.refinementList && indexUiState.refinementList[algoliaAttributes.OperationCode],      
      sortBy: indexUiState.sortBy,
      hitsPerPage: (indexUiState.hitsPerPage && String(indexUiState.hitsPerPage)) || undefined,
      loc: arroundLatLngMatches ? arroundLatLngMatches : undefined,
      fullName: indexUiState.refinementList && indexUiState.refinementList[algoliaAttributes.FullName],
      r: aroundRadiusMatches ? aroundRadiusMatches : undefined,
    };
  },

  routeToState(routeState: RouteState): UiState {

    const refinementList: { [key: string]: string[] } = {};
    //let filters = `groupCode:${routeState.groupCode}`
    let filters = '';
    let aroundLatLng = undefined;
    let aroundRadius = undefined;
    let configure = {};
    let geoSearch = false;
    let geofilters = '';
    let exactfilters = '';
    let geoconfigure = {};
    let exactconfigure = {};

    if (routeState.city) {
      refinementList['address.city'] = routeState.city;
    }

    if (routeState.groupCode && routeState.groupCode.length) {
      filters += filters ? ` AND groupCode:${sanitizeString(routeState.groupCode[0])}` : `groupCode:${sanitizeString(routeState.groupCode[0])}`;
      refinementList[algoliaAttributes.GroupCode] = routeState.groupCode[0].split(',');
    }

    if (routeState.operationName && routeState.operationName.length) {
      //filters += filters ? ` AND operationName:${sanitizeString(routeState.operationName[0])}` : `operationName:${sanitizeString(routeState.operationName[0])}`;
      refinementList[algoliaAttributes.OperationName] = routeState.operationName[0].split(',');
    }

    if (routeState.operationCode && routeState.operationCode.length) {
      filters += filters ? ` AND operationCode:${sanitizeString(routeState.operationCode[0])}` : `operationCode:${sanitizeString(routeState.operationCode[0])}`;
      refinementList[algoliaAttributes.OperationCode] = routeState.operationCode[0].split(',');
    }

    if (routeState.fullName && routeState.fullName.length) {
      refinementList.fullName = routeState.fullName[0].split(',');
    }

    if (routeState.loc && routeState.loc !== 'undefined') {
      geoSearch = true;
      aroundLatLng = routeState.loc;
      aroundRadius = routeState.r;
    }
    else {
      geoSearch = false;
    }

    if (routeState.cityCode && routeState.cityCode.length) {
      const citiesCodes = routeState.cityCode[0].split(',');
      const citiesFilter = citiesCodes.map((cityCode) => {
        return `address.cityCode:${cityCode}`;
      });
      refinementList[algoliaAttributes.AddressCityCode] = citiesCodes;
      if (geoSearch) {
        exactfilters = geofilters = filters;
        exactfilters += filters ? ` AND ${citiesFilter.join(' OR ')}` : `${citiesFilter.join(' OR ')}`;
        geofilters += filters ? ` AND NOT ${citiesFilter.join(' OR ')}` : `NOT ${citiesFilter.join(' OR ')}`;
      }
      else {
        filters += filters ? ` AND (${citiesFilter.join(' OR ')})` : `(${citiesFilter.join(' OR ')})`;
      }
    }

    if (filters) {
      configure = { ...configure, filters };
    }
    if (aroundLatLng) {
      configure = { ...configure, aroundLatLng, aroundRadius };
    }
    if (geofilters) {
      geoconfigure = { ...geoconfigure, filters: geofilters };
      exactconfigure = { ...exactconfigure, filters: exactfilters };
    }
    if (aroundLatLng) {
      exactconfigure = { ...exactconfigure, aroundLatLng, aroundRadius };
      geoconfigure = { ...geoconfigure, aroundLatLng, aroundRadius, getRankingInfo: true };
    }

    if (geoSearch) {
      let modifiedRefList = cloneDeep(refinementList);
      delete modifiedRefList[algoliaAttributes.AddressCityCode];
      return {
        [indexName]: {
          query: routeState.query,
          page: Number(routeState.page),
          refinementList: modifiedRefList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: configure ? configure : undefined,
        },
        ['exact']: {
          query: routeState.query,
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: exactconfigure ? exactconfigure : undefined,
        },
        ['geo']: {
          query: routeState.query,
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: geoconfigure ? geoconfigure : undefined,
        },
      };
    }
    else {
      return {
        [indexName]: {
          query: routeState.query,
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: configure ? configure : undefined,
        }
      };
    }
  },
});

const getRouting = (indexName: string) => ({
  router,
  stateMapping: getStateMapping({ indexName }),
});

export default getRouting;